<template>
<main>
  <Loading v-if="isLoading"></Loading>
  <div v-else class="px-4 py-6">
    <!-- Header -->
    <div class="mb-4">
      <h2 class="text-xl font-semibold text-gray-900">Uptime Monitor</h2>
    </div>

    <!-- Controls: Card Schemes / Toggle / Routes -->
    <div class="flex items-center space-x-2 mb-6">
      <!-- Card Schemes Button -->
      <button
        type="button"
        @click="setTab(true)"
        :class="[controlButtonBase, isCardSchemes ? 'border-current text-current' : 'border-gray-300 text-gray-700']"
        :style="isCardSchemes ? controlActiveStyle : {}"
      >
        CARD SCHEMES
      </button>

      <!-- Toggle -->
      <button @click="toggleTab" class="w-14 focus:outline-none">
        <img class="w-14 inline-block" :src="toggleImg" alt="toggle" />
      </button>

      <!-- Routes Button -->
      <button
        type="button"
        @click="setTab(false)"
        :class="[controlButtonBase, !isCardSchemes ? 'border-current text-current' : 'border-gray-300 text-gray-700']"
        :style="!isCardSchemes ? controlActiveStyle : {}"
      >
        ROUTES
      </button>
    </div>

    <!-- Table -->
    <div class="bg-white rounded">
      <div>
        <table class="min-w-full" style="border:1px solid #EEEDEE;">
          <thead style="background: #F1F5F9; border-bottom: 1px solid #EEEDEE; border-top: 1px solid #EEEDEE;">
            <tr>
              <th class="text-left text-gray-900 text-sm font-semibold px-6 py-4 w-1/3">
                Bank
              </th>
              <th
                v-for="scheme in columns"
                :key="scheme"
                class="text-left text-gray-900 font-semibold px-6 py-4" style="font-size: 14px;"
              >
                {{ scheme }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="row in tableRows" :key="row.name" class="hover:bg-gray-50 border-b" :style="{ borderColor: '#EEEDEE', borderBottomWidth: '1px' }">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">{{ row.name }}</td>
              <td v-for="scheme in columns" :key="row.name + '-' + scheme" class="px-6 py-4">
                <!-- Badge with click-to-toggle tooltip and decorative side bars -->
                <span v-if="row.values[scheme] !== null" class="relative inline-block pr-8">
                  <span :class="[badgeBaseClass(), row.values[scheme] === 0 ? 'justify-start' : 'justify-center']" :style="badgeStyle(row.values[scheme])" @click="toggleTooltip(row.name, scheme)">{{ formatValue(row.values[scheme]) }}</span>
                  <!-- Decorative bars (three thin rounded lines, 70% transparent) -->
                  <div class="absolute top-1/2 right-5 transform -translate-y-1/2 translate-x-2 flex items-center space-x-0.5">
                    <span class="block w-1 h-8 rounded-full" :style="sideDecorStyle(row.values[scheme])"></span>
                    <span class="block w-1 h-8 rounded-full opacity-50" :style="sideDecorStyle(row.values[scheme])"></span>
                    <span class="block w-1 h-6 rounded-full opacity-20" :style="sideDecorStyle(row.values[scheme])"></span>
                  </div>
                  <!-- Tooltip shows only for card-schemes tab -->
                  <div v-if="isCardSchemes && isTooltipOpen(row.name, scheme)"
                       class="absolute left-0 top-full mt-0 bg-white rounded-lg shadow-xl p-4 z-40 block pointer-events-auto"
                       style="width: 420px;">
                    <div class="text-gray-900 font-semibold text-sm">{{ row.name }}/{{ scheme }} <span class="text-gray-400 font-normal">• {{ filteredTransactions(row.name, scheme).length }} transactions</span></div>
                    <div class="text-gray-500 text-sm mt-1">Hover over dots to view transaction ID.</div>
                    <div class="mt-3">
                      <div class="flex flex-wrap">
                        <span v-for="(tx, idx) in filteredTransactions(row.name, scheme)" :key="'tx-'+idx" class="relative mr-1 mb-1 inline-block"
                              @mouseenter="showTxTooltip(tx.transactionId)"
                              @mouseleave="hideTxTooltipDelayed()">
                          <span
                            class="w-3 h-3 rounded-sm inline-block"
                            :style="{ backgroundColor: tx.status === 'SUCCESSFUL' ? '#71DD8C' : (tx.status === 'FAILED' ? '#FF4747' : (tx.status === 'CUSTOMER_INDUCED' ? '#FFCC00' : '#E5E7EB')) }"
                          ></span>
                          <!-- Sub-tooltip for non-success statuses -->
                          <div :class="['absolute left-1/2 -translate-x-1/2 bottom-full mb-0 bg-black bg-opacity-80 text-white rounded-lg shadow-lg px-3 py-2 text-xs whitespace-nowrap z-50 transition-opacity duration-100',
                                 activeTxTooltipId === tx.transactionId ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none']"
                               @mouseenter="showTxTooltip(tx.transactionId)"
                               @mouseleave="hideTxTooltipDelayed()">
                            <div class="opacity-80">Transaction ID</div>
                            <div class="font-semibold tracking-wide">{{ tx.transactionId }}</div>
                            <button type="button" class="underline text-blue-200 ml-2" @click.stop="copyTxId(tx.transactionId)">{{ copiedTxId === tx.transactionId ? 'Copied!' : 'Copy ID' }}</button>
                          </div>
                        </span>
                        <span v-if="!filteredTransactions(row.name, scheme).length" class="text-xs text-gray-500">No transactions</span>
                      </div>
                    </div>
                  </div>
                  <!-- Tooltip for routes tab -->
                  <div v-if="!isCardSchemes && isTooltipOpen(row.name, scheme)"
                       class="absolute left-0 top-full mt-0 bg-white rounded-lg shadow-xl p-4 z-40 block pointer-events-auto"
                       style="width: 420px;">
                    <div class="text-gray-900 font-semibold text-sm">{{ row.name }}/{{ scheme }} <span class="text-gray-400 font-normal">• {{ filteredRouteTransactions(row.name, scheme).length }} transactions</span></div>
                    <div class="text-gray-500 text-sm mt-1">Hover over dots to view transaction ID.</div>
                    <div class="mt-3">
                      <div class="flex flex-wrap">
                        <span v-for="(tx, idx) in filteredRouteTransactions(row.name, scheme)" :key="'rtx-'+idx" class="relative mr-1 mb-1 inline-block"
                              @mouseenter="showTxTooltip(tx.transactionId)"
                              @mouseleave="hideTxTooltipDelayed()">
                          <span
                            class="w-3 h-3 rounded-sm inline-block"
                            :style="{ backgroundColor: tx.status === 'SUCCESSFUL' ? '#71DD8C' : (tx.status === 'FAILED' ? '#FF4747' : (tx.status === 'CUSTOMER_INDUCED' ? '#FFCC00' : '#E5E7EB')) }"
                          ></span>
                          <!-- Sub-tooltip for non-success statuses -->
                          <div :class="['absolute left-1/2 -translate-x-1/2 bottom-full mb-0 bg-black bg-opacity-80 text-white rounded-lg shadow-lg px-3 py-2 text-xs whitespace-nowrap z-50 transition-opacity duration-100',
                                 activeTxTooltipId === tx.transactionId ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none']"
                               @mouseenter="showTxTooltip(tx.transactionId)"
                               @mouseleave="hideTxTooltipDelayed()">
                            <div class="opacity-80">Transaction ID</div>
                            <div class="font-semibold tracking-wide">{{ tx.transactionId }}</div>
                            <button type="button" class="underline text-blue-200 ml-2" @click.stop="copyTxId(tx.transactionId)">{{ copiedTxId === tx.transactionId ? 'Copied!' : 'Copy ID' }}</button>
                          </div>
                        </span>
                        <span v-if="!filteredRouteTransactions(row.name, scheme).length" class="text-xs text-gray-500">No transactions</span>
                      </div>
                    </div>
                  </div>
                </span>
                <span v-else class="text-gray-400">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</main>
</template>

<script>
import ClickOutside from "vue-click-outside";
import Loading from "./general-components/Loading.vue";
export default {
  name: 'TransactionMonitoringPortalTwo',
  data() {
    return {
      isLoading: false,
      schemes: ['Mastercard', 'Visa', 'Verve', 'Afrigo'],
      isCardSchemes: true,
      rows: [],
      apiRows: [],
      thresholds: {
        // Easy to tweak based on PM feedback
        greenMin: 90, // 90–100 -> green
        orangeMin: 70, // 70–89 -> orange, else red
      },
      groupedUsageData: null,
      openTooltipKey: null,
      activeTxTooltipId: null,
      hideTxTooltipTimer: null,
      copiedTxId: null,
      copiedTimer: null,
    };
  },
  components: {
    Loading,
  },
  directives: {
    ClickOutside,
  },
  methods: {
    safePct(val) {
      if (val === null || val === undefined) return null;
      const num = Number(val);
      if (Number.isNaN(num)) return null;
      return num;
    },
    copyTxId(id) {
      try {
        const ta = document.createElement('textarea');
        ta.value = id;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (e) {
        // ignore copy errors silently for now
      }
      this.copiedTxId = id;
      if (this.copiedTimer) clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => {
        this.copiedTxId = null;
        this.copiedTimer = null;
      }, 2000);
    },
    tooltipKey(bankDisplayName, schemeDisplayName) {
      return `${bankDisplayName}__${schemeDisplayName}`;
    },
    isTooltipOpen(bankDisplayName, schemeDisplayName) {
      return this.openTooltipKey === this.tooltipKey(bankDisplayName, schemeDisplayName);
    },
    toggleTooltip(bankDisplayName, schemeDisplayName) {
      const key = this.tooltipKey(bankDisplayName, schemeDisplayName);
      this.openTooltipKey = this.openTooltipKey === key ? null : key;
    },
    formatBankName(slug) {
      if (!slug) return '';
      return slug
        .replace(/_/g, ' ')
        .replace(/\b(\w)/g, (m) => m.toUpperCase());
    },
    // Filter groupedUsageData to the intersecting bank and scheme
    filteredTransactions(bankDisplayName, schemeDisplayName) {
      if (!this.groupedUsageData || !Array.isArray(this.groupedUsageData)) return [];
      // Map display back to API keys
      const bankKey = bankDisplayName.replace(/\s+/g, '_').toUpperCase();
      const schemeKey = schemeDisplayName.toUpperCase();
      const bank = this.groupedUsageData.find(b => (b.bankName || '').toUpperCase() === bankKey);
      if (!bank || !Array.isArray(bank.cardSchemes)) return [];
      const scheme = bank.cardSchemes.find(cs => (cs.cardScheme || '').toUpperCase() === schemeKey);
      if (!scheme || !Array.isArray(scheme.transactions)) return [];
      return scheme.transactions;
    },
    // Filter groupedUsageData to the intersecting bank and route
    filteredRouteTransactions(bankDisplayName, routeDisplayName) {
      if (!this.groupedUsageData || !Array.isArray(this.groupedUsageData)) return [];
      const bankKey = bankDisplayName.replace(/\s+/g, '_').toUpperCase();
      const routeKey = routeDisplayName.toUpperCase();
      const bank = this.groupedUsageData.find(b => (b.bankName || '').toUpperCase() === bankKey);
      if (!bank || !Array.isArray(bank.routes)) return [];
      const route = bank.routes.find(rt => (rt.route || '').toUpperCase() === routeKey);
      if (!route || !Array.isArray(route.transactions)) return [];
      return route.transactions;
    },
    setTab(isCard) {
      this.isCardSchemes = isCard;
      this.fetchMonitoring();
    },
    toggleTab() {
      this.isCardSchemes = !this.isCardSchemes;
      this.fetchMonitoring();
    },
    toggleStatus() {
      this.toggleTab();
    },
    toggleSrc() {
      return this.isCardSchemes ? require('@/assets/toggle-off.png') : require('@/assets/toggle-on.png');
    },
    async fetchMonitoring() {
      console.log('fetchMonitoring', 'isCardSchemes:', this.isCardSchemes);
      this.isLoading = true;
      try {
        let response;
        if (this.isCardSchemes) {
          // Switchable to Kimono endpoints
          response = await this.$store.dispatch('KIMONO_MONITORING_SCHEME_USAGE', {});
        } else {
          response = await this.$store.dispatch('KIMONO_MONITORING_ROUTE_USAGE', {});
        }
        console.log('Monitoring response:', response);
        // Save raw data for normalization in computed properties
        if (response && response.data) {
          this.apiRows = response.data;
        } else if (response && response.status && response.message && response.data === undefined) {
          // In case the action returns axios-like data
          this.apiRows = response;
        }
      } catch (e) {
        // Intentionally swallow for now; user will define handling after response mapping
        console.error('Monitoring fetch error', e);
      } finally {
        this.isLoading = false;
      }
    },
    
    badgeStyle(value) {
      // Use brand hex colors provided by design for the badge background.
      if (value === 0) {
        return { backgroundColor: 'transparent', color: '#111827' };
      }
      if (value >= this.thresholds.greenMin) {
        return { backgroundColor: '#71DD8C', color: '#111827' };
      }
      if (value >= this.thresholds.orangeMin) {
        // Using prior yellow as orange per palette
        return { backgroundColor: '#FFCC00', color: '#111827' };
      }
      return { backgroundColor: '#FF4747', color: '#111827' };
    },
    baseBadgeColor(value) {
      if (value === 0) return '#E5E7EB';
      if (value >= this.thresholds.greenMin) return '#71DD8C';
      if (value >= this.thresholds.orangeMin) return '#FFCC00';
      return '#FF4747';
    },
    hexToRgba(hex, alpha) {
      const clean = hex.replace('#', '');
      const bigint = parseInt(clean, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    sideDecorStyle(value) {
      const base = this.baseBadgeColor(value);
      return { backgroundColor: this.hexToRgba(base, 0.15) };
    },
    showTxTooltip(id) {
      this.activeTxTooltipId = id;
      if (this.hideTxTooltipTimer) {
        clearTimeout(this.hideTxTooltipTimer);
        this.hideTxTooltipTimer = null;
      }
    },
    hideTxTooltipDelayed() {
      if (this.hideTxTooltipTimer) clearTimeout(this.hideTxTooltipTimer);
      this.hideTxTooltipTimer = setTimeout(() => {
        this.activeTxTooltipId = null;
        this.hideTxTooltipTimer = null;
      }, 2000);
    },
    badgeBaseClass() {
      // Base shape/typography; color comes from inline style.
      return 'inline-flex items-center text-left px-3 py-2 rounded text-sm font-medium cursor-pointer w-16';
    },
    
    formatValue(value) {
      // Display to one decimal place only if needed (e.g., 4.5%)
      if (value === 0) return '–';
      const rounded = Math.round(value * 10) / 10;
      const showDecimal = Math.abs(rounded - Math.round(rounded)) > 0.001;
      return `${showDecimal ? rounded.toFixed(1) : Math.round(rounded)}%`;
    },
  },
  computed: {
    // Columns for the right side headers
    columns() {
      return this.isCardSchemes
        ? ['Mastercard', 'Visa', 'Verve', 'Afrigo']
        : ['NIBSS','Kimono','Zone','UPSL'];
    },
    // Normalize scheme-based data to the table format used in the UI
    normalizedSchemeRows() {
      if (!this.apiRows || !Array.isArray(this.apiRows)) return [];
      // Map API bankName and per-scheme percentages to our display format
      return this.apiRows.map((entry) => {
        const name = this.formatBankName(entry.bankName);
        const values = {
          Mastercard: this.safePct(entry.mastercard && entry.mastercard.percentage),
          Visa: this.safePct(entry.visa && entry.visa.percentage),
          Verve: this.safePct(entry.verve && entry.verve.percentage),
          Afrigo: this.safePct(entry.afrigo && entry.afrigo.percentage),
        };
        return { name, values };
      });
    },
    // Normalize route-based data
    normalizedRouteRows() {
      if (!this.apiRows || !Array.isArray(this.apiRows)) return [];
      return this.apiRows.map((entry) => {
        const name = this.formatBankName(entry.bankName);
        const values = {
          Kimono: this.safePct(entry.kimono && entry.kimono.percentage),
          UPSL: this.safePct(entry.upsl && entry.upsl.percentage),
          Zone: this.safePct(entry.zone && entry.zone.percentage),
          NIBSS: this.safePct(entry.nibss && entry.nibss.percentage),
        };
        return { name, values };
      });
    },
    // Rows bound to the table depending on current tab
    tableRows() {
      return this.isCardSchemes ? this.normalizedSchemeRows : this.normalizedRouteRows;
    },
    controlButtonBase() {
      return 'px-2 py-1 rounded border-2 text-xs font-medium tracking-wider uppercase bg-white';
    },
    controlActiveStyle() {
      // Blue outline and text like in the sample image
      return { color: '#243C9A', borderColor: '#243C9A' };
    },
    toggleImg() {
      // When Card Schemes is active, show toggle to the left (off), else to the right (on)
      return this.isCardSchemes ? require('@/assets/toggle-off.png') : require('@/assets/toggle-on.png');
    },
  },
  created() {
    this.fetchMonitoring();
    // Silent preload of grouped usage data
    this.$store
      .dispatch('KIMONO_MONITORING_GROUPED_USAGE', {})
      .then((res) => {
        if (res && res.data) this.groupedUsageData = res.data;
        else this.groupedUsageData = res;
      })
      .catch(() => {
        // ignore silently
      });
  },
};
</script>

<!-- No scoped styles; fully styled with Tailwind v2 utilities -->


